import {
  createDefaultGridV1CellContent,
  normalizeGridV1CellFormat,
  type GridV1CellContent,
} from './grid-v1-cells';
import {
  createDefaultGridV1DocumentValue,
  type GridV1CellFormat,
  type GridV1DocumentValue,
  type GridV1GridOptions,
  type GridV1Widget,
} from './grid-v1-types';

function normalizeCellValue(value: string): string {
  return value.replace(/\r\n/g, '\n');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseGridOptions(raw: unknown): GridV1GridOptions {
  if (!isRecord(raw)) {
    return createDefaultGridV1DocumentValue().grid;
  }

  const grid: GridV1GridOptions = {};
  if (typeof raw.column === 'number') {
    grid.column = raw.column;
  }

  if (
    typeof raw.cellHeight === 'number' ||
    typeof raw.cellHeight === 'string'
  ) {
    grid.cellHeight = raw.cellHeight;
  }

  if (typeof raw.margin === 'number') {
    grid.margin = raw.margin;
  }

  return Object.keys(grid).length > 0
    ? grid
    : createDefaultGridV1DocumentValue().grid;
}

function parseWidget(raw: unknown, index: number): GridV1Widget | null {
  if (!isRecord(raw)) return null;

  const id =
    typeof raw.id === 'string' && raw.id.length > 0
      ? raw.id
      : `cell-${index + 1}`;
  const x = typeof raw.x === 'number' ? raw.x : 0;
  const y = typeof raw.y === 'number' ? raw.y : 0;
  const w = typeof raw.w === 'number' && raw.w > 0 ? raw.w : 4;
  const h = typeof raw.h === 'number' && raw.h > 0 ? raw.h : 2;
  const format = normalizeGridV1CellFormat(raw.format);
  const value =
    typeof raw.value === 'string'
      ? normalizeCellValue(raw.value)
      : createDefaultGridV1CellContent(format).value;

  const widget: GridV1Widget = { id, x, y, w, h, format, value };
  if (typeof raw.minW === 'number') widget.minW = raw.minW;

  if (typeof raw.minH === 'number') widget.minH = raw.minH;

  return widget;
}

export function parseGridV1Document(
  value: string | null | undefined,
): GridV1DocumentValue {
  if (!value || value.trim().length === 0) {
    return createDefaultGridV1DocumentValue();
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(value) as unknown;
  } catch {
    return createDefaultGridV1DocumentValue();
  }

  if (!isRecord(parsed) || parsed.version !== 1) {
    return createDefaultGridV1DocumentValue();
  }

  const grid = parseGridOptions(parsed.grid);
  const rawWidgets = Array.isArray(parsed.widgets) ? parsed.widgets : [];
  const widgets = rawWidgets
    .map((item, index) => parseWidget(item, index))
    .filter((w): w is GridV1Widget => w !== null);

  return { version: 1, grid, widgets };
}

function sortWidgetsById(widgets: GridV1Widget[]): GridV1Widget[] {
  return [...widgets].sort((a, b) => a.id.localeCompare(b.id));
}

export function serializeGridV1Document(doc: GridV1DocumentValue): string {
  const normalized: GridV1DocumentValue = {
    version: 1,
    grid: { ...doc.grid },
    widgets: sortWidgetsById(
      doc.widgets.map((w) => ({
        ...w,
        format: normalizeGridV1CellFormat(w.format),
        value: normalizeCellValue(w.value),
      })),
    ),
  };

  return JSON.stringify(normalized);
}

/** Stable JSON for dirty-state comparison (widgets sorted by id). */
export function gridV1StableStringForDirtyCompare(
  doc: GridV1DocumentValue,
): string {
  return serializeGridV1Document(doc);
}

/**
 * Merge layout-only widgets from GridStack `save(false)` with cell content from state.
 */
export function mergeGridV1LayoutWithContent(
  layoutWidgets: Array<Record<string, unknown>>,
  cellContentById: Map<string, GridV1CellContent>,
  gridOptions: GridV1GridOptions,
): GridV1DocumentValue {
  const widgets: GridV1Widget[] = layoutWidgets
    .map((raw, index) => {
      const id =
        typeof raw.id === 'string' && raw.id.length > 0
          ? raw.id
          : `cell-${index + 1}`;
      const x = typeof raw.x === 'number' ? raw.x : 0;
      const y = typeof raw.y === 'number' ? raw.y : 0;
      const w = typeof raw.w === 'number' && raw.w > 0 ? raw.w : 4;
      const h = typeof raw.h === 'number' && raw.h > 0 ? raw.h : 2;
      const content =
        cellContentById.get(id) ?? createDefaultGridV1CellContent();
      const format: GridV1CellFormat = normalizeGridV1CellFormat(
        content.format,
      );
      const value = normalizeCellValue(content.value);

      const widget: GridV1Widget = { id, x, y, w, h, format, value };
      if (typeof raw.minW === 'number') widget.minW = raw.minW;

      if (typeof raw.minH === 'number') widget.minH = raw.minH;

      return widget;
    })
    .filter((w) => w.id.length > 0);

  return {
    version: 1,
    grid: { ...gridOptions },
    widgets,
  };
}

/** Merge cell content with widget layout already stored in document state (no GridStack save). */
export function mergeGridV1StoredLayoutWithContent(
  doc: GridV1DocumentValue,
  cellContentById: Map<string, GridV1CellContent>,
): GridV1DocumentValue {
  const layoutWidgets = doc.widgets.map((widget) => {
    const raw: Record<string, unknown> = {
      id: widget.id,
      x: widget.x,
      y: widget.y,
      w: widget.w,
      h: widget.h,
    };
    if (typeof widget.minW === 'number') {
      raw.minW = widget.minW;
    }

    if (typeof widget.minH === 'number') {
      raw.minH = widget.minH;
    }

    return raw;
  });

  return mergeGridV1LayoutWithContent(layoutWidgets, cellContentById, doc.grid);
}
